import { useEffect, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { BaseModal, CollaboratorCombobox } from '@/components/shared';
import type { EstadiaCliente, EstadiaClienteUpdate, ProcessoMovimentacao, MetodoEntrega } from '../../../models';
import { useEstadiaClienteMutations } from '../../../hooks/data';
import {
  PROCESSO_MOVIMENTACAO_LABELS,
  PROCESSO_MOVIMENTACAO_OPCOES,
  METODO_ENTREGA_LABELS,
  METODOS_COM_DATA_UNICA,
} from '../equipamentosListPage.definitions';
import { dataInputParaDate, timestampParaDataInput } from '../../../utils/dataInput';

interface EditarEstadiaModalProps {
  isOpen: boolean;
  onClose: () => void;
  estadia: EstadiaCliente;
}

/**
 * Edição completa da estadia (motivo, colaborador cotado, forma/código/data de envio,
 * observações) — aberta ao clicar no card, em vez de só as transições de status via
 * botão. Não mexe em `status`/`motivoEncerramento` (isso continua só pelos botões de ação
 * do card — confirmar chegada, marcar pronta, recolher, cancelar).
 */
export default function EditarEstadiaModal({ isOpen, onClose, estadia }: EditarEstadiaModalProps) {
  const { atualizarEstadia } = useEstadiaClienteMutations();

  const [motivo, setMotivo] = useState<ProcessoMovimentacao>(estadia.motivo);
  const [colaboradorCotadoId, setColaboradorCotadoId] = useState<string | null>(estadia.colaboradorCotadoId ?? null);
  const [formaEnvio, setFormaEnvio] = useState<MetodoEntrega | ''>(estadia.formaEnvio ?? '');
  const [codigoRastreio, setCodigoRastreio] = useState(estadia.codigoRastreio ?? '');
  const [dataEnvio, setDataEnvio] = useState(timestampParaDataInput(estadia.dataEnvioRaro));
  const [formaRecolhimento, setFormaRecolhimento] = useState<MetodoEntrega | ''>(estadia.formaRecolhimento ?? '');
  const [codigoRastreioRecolhimento, setCodigoRastreioRecolhimento] = useState(estadia.codigoRastreioRecolhimento ?? '');
  const [dataRecolhimento, setDataRecolhimento] = useState(timestampParaDataInput(estadia.dataRecolhimento));
  const [observacoes, setObservacoes] = useState(estadia.observacoes ?? '');

  // Recolhimento só faz sentido editar quando a estadia já foi recolhida de volta pra Raro
  // — pra quem foi enviado a um colaborador (Fase 2), data/forma ficam só na Movimentação
  // real, não aqui (evita duplicar a mesma informação em 2 lugares).
  const mostrarRecolhimento = estadia.motivoEncerramento === 'retornado_para_raro';

  // Repopula os campos toda vez que o modal abre — se a estadia mudou (ex. outra ação
  // rodou entre uma abertura e outra), evita mostrar valores desatualizados.
  useEffect(() => {
    if (!isOpen) return;
    setMotivo(estadia.motivo);
    setColaboradorCotadoId(estadia.colaboradorCotadoId ?? null);
    setFormaEnvio(estadia.formaEnvio ?? '');
    setCodigoRastreio(estadia.codigoRastreio ?? '');
    setDataEnvio(timestampParaDataInput(estadia.dataEnvioRaro));
    setFormaRecolhimento(estadia.formaRecolhimento ?? '');
    setCodigoRastreioRecolhimento(estadia.codigoRastreioRecolhimento ?? '');
    setDataRecolhimento(timestampParaDataInput(estadia.dataRecolhimento));
    setObservacoes(estadia.observacoes ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, estadia.id]);

  const usaDataUnica = formaEnvio ? METODOS_COM_DATA_UNICA.includes(formaEnvio) : false;

  const handleSalvar = () => {
    const payload: EstadiaClienteUpdate = {
      id: estadia.id,
      motivo,
      colaboradorCotadoId,
      formaEnvio: formaEnvio || null,
      codigoRastreio: formaEnvio === 'correios' ? codigoRastreio.trim() || null : null,
      observacoes: observacoes.trim() || null,
    };
    if (dataEnvio) {
      const timestamp = Timestamp.fromDate(dataInputParaDate(dataEnvio));
      payload.dataEnvioRaro = timestamp;
      // Uber/pessoalmente: mesmo dia da chegada — atualiza os dois juntos (mesmo padrão
      // da criação, ver useEstadiaClienteMutations.criarEstadias).
      if (usaDataUnica) payload.dataChegadaCliente = timestamp;
    }
    if (mostrarRecolhimento) {
      payload.formaRecolhimento = formaRecolhimento || null;
      payload.codigoRastreioRecolhimento = formaRecolhimento === 'correios' ? codigoRastreioRecolhimento.trim() || null : null;
      if (dataRecolhimento) payload.dataRecolhimento = Timestamp.fromDate(dataInputParaDate(dataRecolhimento));
    }
    atualizarEstadia.mutate(payload, { onSuccess: onClose });
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Editar estadia" maxWidth="max-w-lg" fullHeight={false}>
      <div className="space-y-4 py-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Motivo do envio</label>
          <select
            className="input-primary mt-1 w-full"
            value={motivo}
            onChange={(event) => setMotivo(event.target.value as ProcessoMovimentacao)}
          >
            {PROCESSO_MOVIMENTACAO_OPCOES.map((valor) => (
              <option key={valor} value={valor}>
                {PROCESSO_MOVIMENTACAO_LABELS[valor]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Colaborador cotado</label>
          <CollaboratorCombobox
            selectedId={colaboradorCotadoId}
            onChange={(colaborador) => setColaboradorCotadoId(colaborador?.id ?? null)}
            placeholder="Buscar colaborador..."
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700">Forma de envio</label>
            <select
              className="input-primary mt-1 w-full"
              value={formaEnvio}
              onChange={(event) => setFormaEnvio(event.target.value as MetodoEntrega | '')}
            >
              <option value="">Selecione...</option>
              {(Object.entries(METODO_ENTREGA_LABELS) as [MetodoEntrega, string][]).map(([valor, label]) => (
                <option key={valor} value={valor}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          {formaEnvio === 'correios' && (
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Código de rastreio</label>
              <input
                type="text"
                className="input-primary mt-1 w-full"
                value={codigoRastreio}
                onChange={(event) => setCodigoRastreio(event.target.value)}
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {usaDataUnica ? 'Data de entrega' : 'Data de envio'}
          </label>
          <input
            type="date"
            className="input-primary mt-1 w-full max-w-xs"
            value={dataEnvio}
            onChange={(event) => setDataEnvio(event.target.value)}
          />
        </div>

        {mostrarRecolhimento && (
          <div className="space-y-4 border-t border-gray-100 pt-4">
            <p className="text-sm font-medium text-gray-700">Recolhimento (de volta pra Raro)</p>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">Forma de recolhimento</label>
                <select
                  className="input-primary mt-1 w-full"
                  value={formaRecolhimento}
                  onChange={(event) => setFormaRecolhimento(event.target.value as MetodoEntrega | '')}
                >
                  <option value="">Selecione...</option>
                  {(Object.entries(METODO_ENTREGA_LABELS) as [MetodoEntrega, string][]).map(([valor, label]) => (
                    <option key={valor} value={valor}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {formaRecolhimento === 'correios' && (
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Código de rastreio</label>
                  <input
                    type="text"
                    className="input-primary mt-1 w-full"
                    value={codigoRastreioRecolhimento}
                    onChange={(event) => setCodigoRastreioRecolhimento(event.target.value)}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Data de recolhimento</label>
              <input
                type="date"
                className="input-primary mt-1 w-full max-w-xs"
                value={dataRecolhimento}
                onChange={(event) => setDataRecolhimento(event.target.value)}
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Observações</label>
          <textarea
            className="input-primary mt-1 w-full"
            rows={3}
            value={observacoes}
            onChange={(event) => setObservacoes(event.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={handleSalvar} disabled={atualizarEstadia.isPending}>
            {atualizarEstadia.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
